/**
 * Host-action registry.
 *
 * The agent can emit links with a `rose-action:` scheme (e.g.
 * `rose-action:intercom?space=messages`). The widget intercepts those links and runs a
 * REVIEWED, in-bundle handler instead of navigating — the config/agent only choose WHICH
 * named action runs and its params, never any code. No `eval`, no injected `<script>`; this
 * is deliberately not a channel for arbitrary JavaScript.
 *
 * To add an integration: register one typed handler here (or from a sibling module) and add
 * the matching schema entry. See `docs/plans/ix-3870-intercom-integration.md`.
 */
import { logger } from '../core/logger';

export const HOST_ACTION_SCHEME = 'rose-action:';

export type HostActionParams = Record<string, string>;
export type HostActionContext = {
    trustedParams: HostActionParams;
};
export type HostActionDispatchOptions = {
    trustedParams?: Record<string, HostActionParams>;
};
export type HostActionHandler = (params: HostActionParams, context: HostActionContext) => void;

const registry = new Map<string, HostActionHandler>();

/** Register a named host action. Later registrations override earlier ones. */
export function registerHostAction(name: string, handler: HostActionHandler): void {
    registry.set(name, handler);
}

/** True when a link href targets the host-action scheme. */
export function isHostActionHref(href: string): boolean {
    return href.startsWith(HOST_ACTION_SCHEME);
}

/**
 * Parse a `rose-action:<name>?<query>` href and dispatch to its registered handler.
 * Returns true when an action was matched (whether or not the handler threw).
 */
export function dispatchHostAction(href: string, options: HostActionDispatchOptions = {}): boolean {
    if (!isHostActionHref(href)) {
        return false;
    }
    const rest = href.slice(HOST_ACTION_SCHEME.length);
    const queryStart = rest.indexOf('?');
    const name = queryStart === -1 ? rest : rest.slice(0, queryStart);
    const query = queryStart === -1 ? '' : rest.slice(queryStart + 1);
    const params: HostActionParams = {};
    for (const [key, value] of new URLSearchParams(query)) {
        params[key] = value;
    }

    const handler = registry.get(name);
    if (!handler) {
        logger.warn(`hostAction: no handler registered for "${name}"`);
        return false;
    }
    try {
        handler(params, { trustedParams: options.trustedParams?.[name] ?? {} });
    } catch (error) {
        logger.error(`hostAction "${name}" failed`, error);
    }
    return true;
}

type IntercomFn = (command: string, ...args: unknown[]) => void;
type WindowWithIntercom = Window & { Intercom?: IntercomFn };

/**
 * Resolve the host page's Intercom global.
 *
 * The widget runs in shadow DOM on the host page, so `window.Intercom` is the host global.
 * `window.top` is checked as a fallback and guarded against cross-origin access throwing.
 */
function getIntercom(): IntercomFn | undefined {
    const candidates: WindowWithIntercom[] = [window as WindowWithIntercom];
    try {
        if (window.top && window.top !== window) {
            candidates.push(window.top as WindowWithIntercom);
        }
    } catch {
        // Cross-origin top window — not reachable, ignore.
    }
    for (const candidate of candidates) {
        if (typeof candidate.Intercom === 'function') {
            return candidate.Intercom;
        }
    }
    return undefined;
}

// Built-in: open the host site's Intercom messenger for support hand-off.
registerHostAction('intercom', (params, context) => {
    const intercom = getIntercom();
    if (intercom) {
        intercom('showSpace', params.space || 'messages');
        // Minimize Rose so it does not overlap the Intercom messenger it just opened.
        window.dispatchEvent(new CustomEvent('inboundx_minimize'));
        return;
    }
    if (context.trustedParams.fallback) {
        window.location.href = context.trustedParams.fallback;
        return;
    }
    logger.info(
        'hostAction intercom: window.Intercom not available on this page and no fallback set'
    );
});
